import React, { useState, useMemo } from 'react';
import {
  Briefcase,
  RefreshCw,
  Search,
  Check,
  DollarSign,
  Link as LinkIcon,
  Unlink,
  MoreVertical,
  Filter,
  ChevronDown,
  ChevronUp,
  UserCheck,
} from 'lucide-react';
import { useBusiness } from '@/contexts/BusinessContext';
import {
  useAllProjects,
  useLinkReferralSource,
  useUnlinkReferralSource,
  useMarkPartnerCommissionPaid,
  useMarkPartnerCommissionUnpaid,
  useUpdateProjectSalesRep,
} from '@/hooks/useProjects';
import { useCompanies } from '@/hooks/useCompanies';
import { useSalesReps } from '@/hooks/useUsers';
import { formatCurrency } from '@/services/projects';
import { calculateVisitStatus, getVisitStatusColor, formatDaysSinceVisit } from '@/services/visits';
import { MarkCommissionPaidModal } from '@/components/crm/MarkCommissionPaidModal';
import type { Project, Company, Business, VisitStatus } from '@/types';

// ===========================================
// Types
// ===========================================

type CommissionFilter = 'all' | 'unpaid' | 'paid';
type ReferralFilter = 'all' | 'linked' | 'unlinked';
type SortField = 'name' | 'business' | 'projectType' | 'createdAt' | 'closeDate' | 'totalRevenue' | 'salesRepName' | 'referralSourceName' | 'expectedCommission' | 'partnerExpectedCommission';
type SortOrder = 'asc' | 'desc';

// ===========================================
// Main Component
// ===========================================

export function AllProjectsPage() {
  const { getBusinessLabel, businesses } = useBusiness();
  const [searchQuery, setSearchQuery] = useState('');
  const [businessFilter, setBusinessFilter] = useState<Business | 'all'>('all');
  const [commissionFilter, setCommissionFilter] = useState<CommissionFilter>('all');
  const [referralFilter, setReferralFilter] = useState<ReferralFilter>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [salesRepFilter, setSalesRepFilter] = useState<string>('all');
  const [referralSourceFilter, setReferralSourceFilter] = useState<string>('all');
  const [closeDateFilter, setCloseDateFilter] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showSalesRepModal, setShowSalesRepModal] = useState(false);
  const [showRepCommissionModal, setShowRepCommissionModal] = useState(false);
  const [showPartnerCommissionModal, setShowPartnerCommissionModal] = useState(false);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  // Sort state - default to newest first (createdAt descending)
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const { data: projects, isLoading } = useAllProjects();
  const { data: companies } = useCompanies();
  const { data: salesReps } = useSalesReps();

  // Extract unique project types from projects
  const projectTypes = useMemo(() => {
    if (!projects) return [];
    const types = new Set<string>();
    projects.forEach((p) => {
      if (p.projectType) types.add(p.projectType);
    });
    return Array.from(types).sort();
  }, [projects]);

  // Extract unique referral sources (companies that have been linked)
  const linkedReferralSources = useMemo(() => {
    if (!projects) return [];
    const sources = new Map<string, string>();
    projects.forEach((p) => {
      if (p.referralSourceId && p.referralSourceName) {
        sources.set(p.referralSourceId, p.referralSourceName);
      }
    });
    return Array.from(sources.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  // Generate close date options (months with data)
  const closeDateOptions = useMemo(() => {
    if (!projects) return [];
    const months = new Map<string, string>();
    projects.forEach((p) => {
      if (p.closeDate) {
        const date = new Date(p.closeDate);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        months.set(key, label);
      }
    });
    return Array.from(months.entries())
      .sort((a, b) => b[0].localeCompare(a[0])) // Sort descending (newest first)
      .map(([value, label]) => ({ value, label }));
  }, [projects]);

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    // First filter
    const filtered = projects.filter((project) => {
      // Search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        if (
          !project.name.toLowerCase().includes(search) &&
          !project.referralSourceName?.toLowerCase().includes(search) &&
          !project.salesRepName?.toLowerCase().includes(search)
        ) {
          return false;
        }
      }

      // Business filter
      if (businessFilter !== 'all' && project.business !== businessFilter) {
        return false;
      }

      // Commission filter (partner commission)
      if (commissionFilter === 'paid' && !project.partnerCommissionPaid) {
        return false;
      }
      if (commissionFilter === 'unpaid' && project.partnerCommissionPaid) {
        return false;
      }

      // Referral filter (linked/unlinked)
      if (referralFilter === 'linked' && !project.referralSourceId) {
        return false;
      }
      if (referralFilter === 'unlinked' && project.referralSourceId) {
        return false;
      }

      // Type filter
      if (typeFilter !== 'all' && project.projectType !== typeFilter) {
        return false;
      }

      // Sales Rep filter
      if (salesRepFilter !== 'all' && project.salesRepUserId !== salesRepFilter) {
        return false;
      }

      // Referral Source filter (specific company)
      if (referralSourceFilter !== 'all' && project.referralSourceId !== referralSourceFilter) {
        return false;
      }

      // Close Date filter (by month)
      if (closeDateFilter !== 'all' && project.closeDate) {
        const date = new Date(project.closeDate);
        const projectMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (projectMonth !== closeDateFilter) {
          return false;
        }
      } else if (closeDateFilter !== 'all' && !project.closeDate) {
        return false;
      }

      return true;
    });

    // Then sort
    return [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'business':
          aValue = a.business || '';
          bValue = b.business || '';
          break;
        case 'projectType':
          aValue = a.projectType?.toLowerCase() || '';
          bValue = b.projectType?.toLowerCase() || '';
          break;
        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'closeDate':
          aValue = a.closeDate ? new Date(a.closeDate).getTime() : 0;
          bValue = b.closeDate ? new Date(b.closeDate).getTime() : 0;
          break;
        case 'totalRevenue':
          aValue = a.totalRevenue || 0;
          bValue = b.totalRevenue || 0;
          break;
        case 'salesRepName':
          aValue = a.salesRepName?.toLowerCase() || '';
          bValue = b.salesRepName?.toLowerCase() || '';
          break;
        case 'referralSourceName':
          aValue = a.referralSourceName?.toLowerCase() || '';
          bValue = b.referralSourceName?.toLowerCase() || '';
          break;
        case 'expectedCommission':
          aValue = a.expectedCommission || 0;
          bValue = b.expectedCommission || 0;
          break;
        case 'partnerExpectedCommission':
          aValue = a.partnerExpectedCommission || 0;
          bValue = b.partnerExpectedCommission || 0;
          break;
        default:
          return 0;
      }

      // Handle null/undefined - push to end
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare
      let comparison = 0;
      if (typeof aValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = aValue - bValue;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [projects, searchQuery, businessFilter, commissionFilter, referralFilter, typeFilter, salesRepFilter, referralSourceFilter, closeDateFilter, sortField, sortOrder]);

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field - default to descending for dates/numbers, ascending for text
      setSortField(field);
      const numericFields: SortField[] = ['createdAt', 'closeDate', 'totalRevenue', 'expectedCommission', 'partnerExpectedCommission'];
      setSortOrder(numericFields.includes(field) ? 'desc' : 'asc');
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    if (!filteredProjects.length) {
      return {
        totalProjects: 0,
        totalRevenue: 0,
        totalPartnerCommission: 0,
        paidPartnerCommission: 0,
        unpaidPartnerCommission: 0,
      };
    }

    let totalRevenue = 0;
    let totalPartnerCommission = 0;
    let paidPartnerCommission = 0;

    for (const project of filteredProjects) {
      totalRevenue += project.totalRevenue;
      totalPartnerCommission += project.partnerExpectedCommission;
      if (project.partnerCommissionPaid) {
        paidPartnerCommission +=
          project.partnerPaidAmount || project.partnerExpectedCommission;
      }
    }

    return {
      totalProjects: filteredProjects.length,
      totalRevenue,
      totalPartnerCommission,
      paidPartnerCommission,
      unpaidPartnerCommission: totalPartnerCommission - paidPartnerCommission,
    };
  }, [filteredProjects]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">All Projects</h1>
          <p className="page-description">
            Manage all projects and partner commissions across businesses.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Projects"
          value={stats.totalProjects.toString()}
          icon={<Briefcase size={20} />}
        />
        <StatCard
          label="Total Revenue"
          value={formatCurrency(stats.totalRevenue)}
          icon={<DollarSign size={20} />}
        />
        <StatCard
          label="Partner Commissions"
          value={formatCurrency(stats.totalPartnerCommission)}
          subtitle={`${formatCurrency(stats.paidPartnerCommission)} paid`}
          icon={<DollarSign size={20} />}
        />
        <StatCard
          label="Unpaid Partner"
          value={formatCurrency(stats.unpaidPartnerCommission)}
          className={stats.unpaidPartnerCommission > 0 ? 'text-orange-600' : ''}
          icon={<DollarSign size={20} />}
        />
      </div>

      {/* Filters */}
      <div className="card space-y-4">
        {/* Row 1: Search and Business */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search projects, partners, or reps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Business Filter */}
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Business:</span>
            <select
              value={businessFilter}
              onChange={(e) => setBusinessFilter(e.target.value as Business | 'all')}
              className="input w-auto"
            >
              <option value="all">All Businesses</option>
              {businesses.map((b) => (
                <option key={b} value={b}>
                  {getBusinessLabel(b)}
                </option>
              ))}
            </select>
          </div>

          {/* Commission Filter */}
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Commission:</span>
            <select
              value={commissionFilter}
              onChange={(e) => setCommissionFilter(e.target.value as CommissionFilter)}
              className="input w-auto"
            >
              <option value="all">All</option>
              <option value="unpaid">Unpaid Only</option>
              <option value="paid">Paid Only</option>
            </select>
          </div>

          {/* Referral Linked Filter */}
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Referral:</span>
            <select
              value={referralFilter}
              onChange={(e) => setReferralFilter(e.target.value as ReferralFilter)}
              className="input w-auto"
            >
              <option value="all">All</option>
              <option value="linked">Linked</option>
              <option value="unlinked">Unlinked</option>
            </select>
          </div>
        </div>

        {/* Row 2: Type, Close Date, Sales Rep, Referral Source */}
        <div className="flex flex-wrap gap-4">
          {/* Type Filter */}
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Type:</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="all">All</option>
              {projectTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Close Date Filter */}
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Close Date:</span>
            <select
              value={closeDateFilter}
              onChange={(e) => setCloseDateFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="all">All</option>
              {closeDateOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sales Rep Filter */}
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Sales Rep:</span>
            <select
              value={salesRepFilter}
              onChange={(e) => setSalesRepFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="all">All</option>
              {salesReps?.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.displayName}
                </option>
              ))}
            </select>
          </div>

          {/* Referral Source Filter */}
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Referral Source:</span>
            <select
              value={referralSourceFilter}
              onChange={(e) => setReferralSourceFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="all">All</option>
              {linkedReferralSources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Projects Table */}
      {isLoading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
          </div>
        </div>
      ) : filteredProjects.length > 0 ? (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr className="group">
                <SortableHeader label="Project" field="name" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Business" field="business" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Type" field="projectType" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Created" field="createdAt" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Close Date" field="closeDate" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Revenue" field="totalRevenue" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Sales Rep" field="salesRepName" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Referral Source" field="referralSourceName" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <th>Visit Status</th>
                <SortableHeader label="Rep Commission" field="expectedCommission" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Partner Commission" field="partnerExpectedCommission" currentSortField={sortField} currentSortOrder={sortOrder} onSort={handleSort} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  companies={companies || []}
                  getBusinessLabel={getBusinessLabel}
                  actionMenuId={actionMenuId}
                  setActionMenuId={setActionMenuId}
                  onLinkReferral={() => {
                    setSelectedProject(project);
                    setShowLinkModal(true);
                  }}
                  onPayPartner={() => {
                    setSelectedProject(project);
                    setShowPartnerCommissionModal(true);
                  }}
                  onMarkRepCommissionPaid={() => {
                    setSelectedProject(project);
                    setShowRepCommissionModal(true);
                  }}
                  onChangeSalesRep={() => {
                    setSelectedProject(project);
                    setShowSalesRepModal(true);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Briefcase size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">No projects found</p>
            <p className="mt-1">
              {searchQuery || businessFilter !== 'all' || commissionFilter !== 'all' || referralFilter !== 'all' || typeFilter !== 'all' || salesRepFilter !== 'all' || referralSourceFilter !== 'all' || closeDateFilter !== 'all'
                ? 'Try adjusting your filters.'
                : 'Projects will appear here after Portal sync.'}
            </p>
          </div>
        </div>
      )}

      {/* Link Referral Modal */}
      {showLinkModal && selectedProject && (
        <LinkReferralModal
          project={selectedProject}
          companies={companies || []}
          onClose={() => {
            setShowLinkModal(false);
            setSelectedProject(null);
          }}
        />
      )}

      {/* Sales Rep Commission Modal */}
      {showRepCommissionModal && selectedProject && (
        <MarkCommissionPaidModal
          project={selectedProject}
          type="sales-rep"
          onClose={() => {
            setShowRepCommissionModal(false);
            setSelectedProject(null);
          }}
          onSuccess={() => {
            // Refresh will happen automatically via React Query
          }}
        />
      )}

      {/* Partner Commission Modal */}
      {showPartnerCommissionModal && selectedProject && (
        <MarkCommissionPaidModal
          project={selectedProject}
          type="partner"
          onClose={() => {
            setShowPartnerCommissionModal(false);
            setSelectedProject(null);
          }}
          onSuccess={() => {
            // Refresh will happen automatically via React Query
          }}
        />
      )}

      {/* Pay Partner Commission Modal - Legacy (can be removed if using above) */}
      {showPayModal && selectedProject && (
        <PayPartnerModal
          project={selectedProject}
          onClose={() => {
            setShowPayModal(false);
            setSelectedProject(null);
          }}
        />
      )}

      {/* Change Sales Rep Modal */}
      {showSalesRepModal && selectedProject && (
        <ChangeSalesRepModal
          project={selectedProject}
          salesReps={salesReps || []}
          onClose={() => {
            setShowSalesRepModal(false);
            setSelectedProject(null);
          }}
        />
      )}
    </div>
  );
}

// ===========================================
// Sortable Header Component
// ===========================================

interface SortableHeaderProps {
  label: string;
  field: SortField;
  currentSortField: SortField;
  currentSortOrder: SortOrder;
  onSort: (field: SortField) => void;
}

function SortableHeader({ label, field, currentSortField, currentSortOrder, onSort }: SortableHeaderProps) {
  const isActive = currentSortField === field;

  return (
    <th
      onClick={() => onSort(field)}
      className="cursor-pointer hover:bg-gray-100 select-none transition-colors"
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className={`transition-opacity ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
          {isActive ? (
            currentSortOrder === 'asc' ? (
              <ChevronUp size={14} className="text-blue-600" />
            ) : (
              <ChevronDown size={14} className="text-blue-600" />
            )
          ) : (
            <ChevronDown size={14} className="text-gray-400" />
          )}
        </span>
      </div>
    </th>
  );
}

// ===========================================
// Stat Card Component
// ===========================================

interface StatCardProps {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  className?: string;
}

function StatCard({ label, value, subtitle, icon, className = '' }: StatCardProps) {
  return (
    <div className="card">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-50 rounded-lg text-blue-600">{icon}</div>
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className={`text-xl font-semibold ${className}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

// ===========================================
// Project Row Component
// ===========================================

interface ProjectRowProps {
  project: Project;
  companies: Company[];
  getBusinessLabel: (b: Business) => string;
  actionMenuId: string | null;
  setActionMenuId: (id: string | null) => void;
  onLinkReferral: () => void;
  onPayPartner: () => void;
  onMarkRepCommissionPaid: () => void;
  onChangeSalesRep: () => void;
}

function ProjectRow({
  project,
  companies,
  getBusinessLabel,
  actionMenuId,
  setActionMenuId,
  onLinkReferral,
  onPayPartner,
  onMarkRepCommissionPaid,
  onChangeSalesRep,
}: ProjectRowProps) {
  const unlinkMutation = useUnlinkReferralSource();
  const markUnpaidMutation = useMarkPartnerCommissionUnpaid();

  // Find the linked company to get visit status
  const linkedCompany = project.referralSourceId
    ? companies.find((c) => c.id === project.referralSourceId)
    : null;
  const visitInfo = linkedCompany
    ? calculateVisitStatus(linkedCompany.lastVisitDate || null)
    : null;
  const visitColors = visitInfo ? getVisitStatusColor(visitInfo.status) : null;

  const formatDate = (date: Date | undefined | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleUnlink = async () => {
    await unlinkMutation.mutateAsync(project.id);
    setActionMenuId(null);
  };

  const handleMarkUnpaid = async () => {
    await markUnpaidMutation.mutateAsync(project.id);
    setActionMenuId(null);
  };

  return (
    <tr>
      <td className="font-medium text-gray-900">{project.name}</td>
      <td>
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            project.business === 'azdisaster'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-purple-100 text-purple-800'
          }`}
        >
          {getBusinessLabel(project.business)}
        </span>
      </td>
      <td className="text-sm text-gray-600">{project.projectType}</td>
      <td className="text-sm text-gray-600">{formatDate(project.createdAt)}</td>
      <td className="text-sm text-gray-600">{formatDate(project.closeDate)}</td>
      <td className="font-mono text-sm">{formatCurrency(project.totalRevenue)}</td>
      <td className="text-sm text-gray-600">{project.salesRepName || '—'}</td>
      <td>
        {project.referralSourceName ? (
          <div>
            <span className="text-sm text-gray-900">{project.referralSourceName}</span>
            {project.referralContactName && (
              <span className="block text-xs text-gray-500">{project.referralContactName}</span>
            )}
          </div>
        ) : (
          <span className="text-sm text-gray-400 italic">None linked</span>
        )}
      </td>
      <td>
        {visitInfo && visitColors ? (
          <div className="flex items-center gap-2">
            <div
              className={`w-3 h-3 rounded-full ${visitColors.dot}`}
              title={`${visitInfo.status === 'green' ? 'Recently visited' : visitInfo.status === 'yellow' ? 'Visit soon' : 'Needs visit'}`}
            />
            <span className={`text-xs ${visitColors.text}`}>
              {formatDaysSinceVisit(visitInfo.daysSince)}
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
      <td>
        <CommissionBadge
          amount={project.expectedCommission}
          paid={project.commissionPaid}
          paidAmount={project.paidAmount}
        />
      </td>
      <td>
        <CommissionBadge
          amount={project.partnerExpectedCommission}
          paid={project.partnerCommissionPaid}
          paidAmount={project.partnerPaidAmount}
          isPartner
        />
      </td>
      <td>
        <div className="relative">
          <button
            onClick={() => setActionMenuId(actionMenuId === project.id ? null : project.id)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          >
            <MoreVertical size={18} />
          </button>

          {actionMenuId === project.id && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setActionMenuId(null)}
              />
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border z-50">
                {!project.referralSourceId ? (
                  <button
                    onClick={() => {
                      onLinkReferral();
                      setActionMenuId(null);
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LinkIcon size={16} />
                    Link Referral Partner
                  </button>
                ) : (
                  <button
                    onClick={handleUnlink}
                    disabled={unlinkMutation.isPending}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Unlink size={16} />
                    Unlink Partner
                  </button>
                )}

                {project.referralSourceId && project.partnerExpectedCommission > 0 && (
                  <>
                    {!project.partnerCommissionPaid ? (
                      <button
                        onClick={() => {
                          onPayPartner();
                          setActionMenuId(null);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-green-700 hover:bg-gray-100"
                      >
                        <DollarSign size={16} />
                        Mark Partner Paid
                      </button>
                    ) : (
                      <button
                        onClick={handleMarkUnpaid}
                        disabled={markUnpaidMutation.isPending}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-orange-700 hover:bg-gray-100"
                      >
                        <DollarSign size={16} />
                        Mark Partner Unpaid
                      </button>
                    )}
                  </>
                )}

                {project.expectedCommission > 0 && !project.commissionPaid && (
                  <button
                    onClick={() => {
                      onMarkRepCommissionPaid();
                      setActionMenuId(null);
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-blue-700 hover:bg-gray-100"
                  >
                    <DollarSign size={16} />
                    Mark Rep Commission Paid
                  </button>
                )}

                <button
                  onClick={() => {
                    onChangeSalesRep();
                    setActionMenuId(null);
                  }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-blue-700 hover:bg-gray-100"
                >
                  <UserCheck size={16} />
                  Change Sales Rep
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ===========================================
// Commission Badge Component
// ===========================================

interface CommissionBadgeProps {
  amount: number;
  paid: boolean;
  paidAmount?: number | null;
  isPartner?: boolean;
}

function CommissionBadge({ amount, paid, paidAmount, isPartner }: CommissionBadgeProps) {
  if (amount === 0) {
    return <span className="text-gray-400 text-sm">—</span>;
  }

  return (
    <div className="flex items-center gap-1">
      <span className={`font-mono text-sm ${paid ? 'text-green-700' : 'text-gray-900'}`}>
        {formatCurrency(paidAmount || amount)}
      </span>
      {paid ? (
        <Check size={14} className="text-green-600" />
      ) : (
        <span className="w-2 h-2 rounded-full bg-orange-400" />
      )}
    </div>
  );
}

// ===========================================
// Link Referral Modal
// ===========================================

interface LinkReferralModalProps {
  project: Project;
  companies: Company[];
  onClose: () => void;
}

function LinkReferralModal({ project, companies, onClose }: LinkReferralModalProps) {
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const linkMutation = useLinkReferralSource();

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;

    await linkMutation.mutateAsync({
      projectId: project.id,
      referralSourceId: selectedCompany.id,
      referralSourceName: selectedCompany.name,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900">Link Referral Partner</h3>
        <p className="mt-1 text-sm text-gray-600">
          Link a referral partner to <strong>{project.name}</strong>
        </p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Partner
            </label>
            <select
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              className="input"
              required
            >
              <option value="">Choose a partner...</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                  {company.type ? ` (${company.type})` : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedCompany && selectedCompany.commissionValue && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">Partner Commission Rate:</p>
              <p className="font-medium">
                {selectedCompany.commissionType === 'percentage'
                  ? `${(selectedCompany.commissionValue * 100).toFixed(1)}%`
                  : formatCurrency(selectedCompany.commissionValue)}
                {selectedCompany.commissionCap && (
                  <span className="text-gray-500 text-sm ml-1">
                    (cap: {formatCurrency(selectedCompany.commissionCap)})
                  </span>
                )}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedCompanyId || linkMutation.isPending}
              className="btn btn-primary flex-1"
            >
              {linkMutation.isPending ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Linking...
                </>
              ) : (
                <>
                  <LinkIcon size={18} />
                  Link Partner
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================
// Pay Partner Modal
// ===========================================

interface PayPartnerModalProps {
  project: Project;
  onClose: () => void;
}

function PayPartnerModal({ project, onClose }: PayPartnerModalProps) {
  const [paidAmount, setPaidAmount] = useState(project.partnerExpectedCommission.toString());
  const markPaidMutation = useMarkPartnerCommissionPaid();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await markPaidMutation.mutateAsync({
      projectId: project.id,
      paidAmount: parseFloat(paidAmount) || project.partnerExpectedCommission,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900">Mark Partner Commission Paid</h3>
        <p className="mt-1 text-sm text-gray-600">
          Record partner payment for <strong>{project.name}</strong>
        </p>

        <div className="mt-4 bg-gray-50 rounded-lg p-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Partner:</span>
            <span className="font-medium">{project.referralSourceName}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-gray-600">Expected Commission:</span>
            <span className="font-medium">
              {formatCurrency(project.partnerExpectedCommission)}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount Paid ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
              className="input"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Defaults to expected commission. Adjust if different.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={markPaidMutation.isPending}
              className="btn btn-primary flex-1"
            >
              {markPaidMutation.isPending ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Mark Paid
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================
// Change Sales Rep Modal
// ===========================================

interface ChangeSalesRepModalProps {
  project: Project;
  salesReps: Array<{ id: string; displayName: string; email: string }>;
  onClose: () => void;
}

function ChangeSalesRepModal({ project, salesReps, onClose }: ChangeSalesRepModalProps) {
  const [selectedRepId, setSelectedRepId] = useState(project.salesRepUserId || '');
  const updateMutation = useUpdateProjectSalesRep();

  const selectedRep = salesReps.find((r) => r.id === selectedRepId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await updateMutation.mutateAsync({
      projectId: project.id,
      salesRepUserId: selectedRepId || null,
      salesRepName: selectedRep?.displayName || null,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900">Change Sales Rep</h3>
        <p className="mt-1 text-sm text-gray-600">
          Assign a sales rep to <strong>{project.name}</strong>
        </p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sales Rep
            </label>
            <select
              value={selectedRepId}
              onChange={(e) => setSelectedRepId(e.target.value)}
              className="input"
            >
              <option value="">None (unassign)</option>
              {salesReps.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.displayName} ({rep.email})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {project.salesRepName
                ? `Currently: ${project.salesRepName}`
                : 'No sales rep currently assigned'}
            </p>
          </div>

          {selectedRep && (
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-sm text-gray-700">
                <strong>{selectedRep.displayName}</strong> will be assigned to this project and
                will receive commission based on the configured rules.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="btn btn-primary flex-1"
            >
              {updateMutation.isPending ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <UserCheck size={18} />
                  Update Sales Rep
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
